import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import AeroIcon from './AeroIcon';
import ShareDialogMock from '../ViewportEditor/ShareDialogMock';
import currentOwnerName from '../../utils/shareGuideOwner';
import useSharePolling from '../../utils/useSharePolling';
import { GUIDE_COPY as C } from '../../utils/figmaLinkCopy';
import './AeroShareGuideModal.css';

/**
 * AeroShareGuideModal: "Share your prototype with everyone". One short looping animation, three
 * steps, and it checks again by itself, so there is no button to press afterwards. Aero's look;
 * Figmafolio's twin is ViewportEditor/ShareGuideModal.js. Same words (utils/figmaLinkCopy.js),
 * same polling (utils/useSharePolling.js: every 3 seconds, on focus, stops after 2 minutes, closes
 * itself when it turns shared).
 *
 * The screens are drawn (ViewportEditor/ShareDialogMock), not screenshots, with an outline on top.
 * Update the drawing if Figma's sharing steps change.
 *
 *   show, onClose, fileName (optional: the user's own file name, shown in the drawn dialog)
 *   recheck()   async, returns 'viewable' | 'not_viewable' | 'unknown'
 *   onShared()  called once, when it turns viewable
 *
 * Portalled, so it carries `ut-theme` on its own root (the .aero-modal pattern).
 */
export default function AeroShareGuideModal({ show, onClose, recheck, onShared, fileName }) {
  const ownerName = currentOwnerName();
  const { done } = useSharePolling({ show, recheck, onShared, onClose });

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, onClose]);

  if (!show) return null;
  return ReactDOM.createPortal(
    <div className="ut-modal-root aero-modal-backdrop ut-theme" onClick={onClose}>
      <div
        className="aero-modal aero-sg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aero-sg-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aero-modal-head">
          <h2 id="aero-sg-title">{C.title}</h2>
          <button type="button" className="aero-btn aero-btn-ghost aero-btn-icon" onClick={onClose} aria-label="Close">
            <AeroIcon name="x" />
          </button>
        </div>

        {done ? (
          <div className="aero-modal-body aero-sg-done" role="status">
            <span className="aero-sg-done-mark" aria-hidden="true"><AeroIcon name="check" /></span>
            <b>{C.doneTitle}</b>
            <span>{C.doneSub}</span>
          </div>
        ) : (
          <div className="aero-modal-body">
            <div className="aero-sg-stage">
              <div className="aero-sg-scene aero-sg-a">
                <div className="aero-sg-shot aero-sg-wide aero-sg-zoom-a">
                  <ShareDialogMock scene="a" fileName={fileName} ownerName={ownerName} />
                  <span className="aero-sg-ring aero-sg-ring1" aria-hidden="true" />
                </div>
              </div>
              <div className="aero-sg-scene aero-sg-b">
                <div className="aero-sg-shot aero-sg-tall">
                  <ShareDialogMock scene="b" ownerName={ownerName} />
                  <span className="aero-sg-ring aero-sg-ring2" aria-hidden="true" />
                  <span className="aero-sg-ring aero-sg-ring3" aria-hidden="true" />
                  <span className="aero-sg-ring aero-sg-ring4" aria-hidden="true" />
                </div>
              </div>
              <div className="aero-sg-scene aero-sg-c">
                <div className="aero-sg-shot aero-sg-wide aero-sg-zoom-c">
                  <ShareDialogMock scene="c" fileName={fileName} ownerName={ownerName} />
                  <span className="aero-sg-ring aero-sg-ring5" aria-hidden="true" />
                </div>
              </div>
              <div className="aero-sg-toast" aria-hidden="true">{C.toast}</div>
            </div>

            <div className="aero-sg-caps" aria-live="polite">
              {C.steps.map(([title, sub], i) => (
                <div key={title} className={`aero-sg-cap aero-sg-cap-${'abc'[i]}`}>
                  <b>{title}</b>
                  <span>{sub}</span>
                </div>
              ))}
            </div>
            <p className="aero-sg-foot">{C.footer}</p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
